import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { IAnalysedProperty } from "../interface/addAnalyser";
import {
  ARCHISTAR_URL,
  RPDATA_URL,
  CLASSIC_SITE_HOME_URL_AU,
  CLASSIC_SITE_HOME_URL_NZ,
  COUNTRY_REGEX,
  LEGACY_REDIRECT_URL,
  MEMBER_KEY,
  POSTCODE_REGEX,
  TOKEN_KEY,
} from "./constants";

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatCurrencyWithDecimal = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatNumber = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatNumberWithDecimal = (
  amount: number,
  MinFraction: number | undefined,
  MaxFraction: number | undefined
): string => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: MinFraction,
    maximumFractionDigits: MaxFraction,
  }).format(amount);
};

export const toPascalCase = (str: string): string => {
  if (typeof str !== "string") {
    return str;
  }
  return str
    .replace(/[-_ ]+/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join("");
};

export const formatDayCycle = (dayCycle: number): string => {
  switch (dayCycle) {
    case 1:
      return `${dayCycle}st`;
    case 2:
      return `${dayCycle}nd`;
    case 3:
      return `${dayCycle}rd`;
    default:
      return `${dayCycle}th`;
  }
};

export const formatDate = (date: string): string => {
  return new Date(date).toLocaleDateString("en-GB");
};

export const getDateStringWithGivenFormat = (
  date: Date,
  format: string | "dd/mm/yy" | "mm/dd/yy" | "yyyy/mm/dd" = "mm/dd/yy"
): string => {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error("Invalid date provided");
  }
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear());

  switch (format) {
    case "dd/mm/yy":
      return `${day}/${month}/${year}`;
    case "mm/dd/yy":
      return `${month}/${day}/${year}`;
    case "yyyy/mm/dd":
      return `${year}/${month}/${day}`;
    default:
      return `${month}/${day}/${year}`;
  }
};

const adjustDate = (date: Date, adjustment: { days?: number; months?: number; years?: number }) => {
  const newDate = new Date(date);
  if (adjustment.days) {
    newDate.setDate(newDate.getDate() + adjustment.days);
  }
  if (adjustment.months) {
    newDate.setMonth(newDate.getMonth() + adjustment.months);
  }
  if (adjustment.years) {
    newDate.setFullYear(newDate.getFullYear() + adjustment.years);
  }
  return newDate;
};

export const getAdjustedFormattedDate = (
  date: Date,
  adjustment: { days?: number; months?: number; years?: number }
) => {
  const adjustedDate = adjustDate(date, adjustment);
  return getDateStringWithGivenFormat(adjustedDate);
};

export const downloadPdf = (fileName: string, base64Data: string) => {
  const linkSource = `data:application/pdf;base64,${base64Data}`;
  const downloadLink = document.createElement("a");
  const fileNameToDownload = fileName || "invoice.pdf";

  downloadLink.href = linkSource;
  downloadLink.download = fileNameToDownload;
  downloadLink.click();
};

export const nth = (n: number) => {
  if (n > 3 && n < 21) return "th";
  switch (n % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export const calculatePercentageShare = (data: any[], labels: string[]): number[] => {
  const numericData = data.filter((item) => typeof item === "number");

  const total = numericData.reduce((sum, value) => sum + value, 0);

  const percentagesInfo = numericData.map((value, currentIndex) => {
    const exactPercent = (value / total) * 100;
    return {
      index: currentIndex,
      rounded: Math.round(exactPercent),
      exact: exactPercent,
    };
  });

  const roundedSum = percentagesInfo.reduce((sum, info) => sum + info.rounded, 0);

  if (roundedSum !== 100) {
    const diff = 100 - roundedSum;

    const sortedIndices = percentagesInfo
      .map((info, index) => ({ index, diff: info.exact - info.rounded }))
      .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));

    for (let i = 0; i < Math.abs(diff); i++) {
      percentagesInfo[sortedIndices[i].index].rounded += diff > 0 ? 1 : -1;
    }
  }
  const result: any[] = new Array(labels.length).fill(0);

  percentagesInfo.forEach((info) => {
    result[info.index] = info.rounded;
  });

  const populationIndex = labels.findIndex((label) => label === "Population");
  if (populationIndex !== -1 && data[populationIndex] !== undefined) {
    const percentageToMove = result[populationIndex];

    const newIndex = result.findIndex((value, idx) => value === 0 && idx !== populationIndex);
    if (newIndex !== -1) {
      result[newIndex] = percentageToMove;
    }

    result[populationIndex] = data[populationIndex];
  }
  return result;
};

export const sortObjectByNumericKeys = (obj: Record<string, any>): Record<string, any> => {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }
  return Object.fromEntries(
    Object.entries(obj).sort((a, b) => parseInt(a[0].replace(/\D/g, "")) - parseInt(b[0].replace(/\D/g, ""))) // Sort by numeric part
  );
};

export const useGoogleMapsLoader = (): boolean => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const existingScript = document.getElementById("google-maps-script");

    if (existingScript) {
      existingScript.addEventListener("load", () => setIsLoaded(true));
      if ((window as any).google) {
        setIsLoaded(true);
      }
    } else {
      const script = document.createElement("script");
      script.id = "google-maps-script";
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.GOOGLE_MAPS_API_KEY}&libraries=places,marker&v=beta&loading=asyn`;
      script.async = true;
      script.defer = true;

      script.onload = () => setIsLoaded(true);
      script.onerror = () => console.error("Failed to load Google Maps API script");

      document.head.appendChild(script);
    }
  }, []);

  return isLoaded;
};

export const redirectToArchistar = () => {
  const memberId = localStorage.getItem("member_id");
  if (memberId) {
    window.open(
      `https://www.realestateinvestar.com.au/ArchiStarAPI/AutoLogIn.aspx?memberid=${memberId}&token=HhkgLGTYkjTRLT1032`,
      "_blank"
    );
  }
};

export const hasRPDataActive = () => localStorage.getItem("isRPDataActive") === "true";
export const hasArchistarActive = () => localStorage.getItem("isArchistarActive") === "true";
export const isStarterPlan = () => localStorage.getItem("IS_MONTHLY_CASUAL_PLAN") === "true";
export const openRPDataLogin = () => window.open(RPDATA_URL, "_blank", "noopener,noreferrer");
export const openArchistarLogin = () => window.open(ARCHISTAR_URL, "_blank", "noopener,noreferrer");
export const hideUpgradePanel = true;

export const redirectToRPData = () => {
  const memberId = localStorage.getItem("member_id");
  if (memberId) {
    window.open(
      `https://www.realestateinvestar.com.au/PriceFinderAPI/AutoLogIn.aspx?memberid=${memberId}&token=rpaHyJKMsYGS197345324`,
      "_blank"
    );
  }
};

export const redirectToClassicSite = () => {
  const authToken = localStorage.getItem(TOKEN_KEY);
  const memberId = localStorage.getItem(MEMBER_KEY);
  const countryID = localStorage.getItem("country_id");

  const legacyTargetUrl = countryID === "NZ" ? CLASSIC_SITE_HOME_URL_NZ : CLASSIC_SITE_HOME_URL_AU;
  const classicSiteUrl = new URL(LEGACY_REDIRECT_URL);

  if (authToken && memberId) {
    classicSiteUrl.searchParams.set("AuthToken", authToken);
    classicSiteUrl.searchParams.set("MemberID", memberId);
    classicSiteUrl.searchParams.set("authToken", authToken);
    classicSiteUrl.searchParams.set("memberId", memberId);
  }

  classicSiteUrl.searchParams.set("redirectUrl", legacyTargetUrl);

  const url = classicSiteUrl.toString();

  window.open(url, "_blank", "noopener,noreferrer");
  return;
};

export const isPropertyDetailValid = (analysedProperty: IAnalysedProperty | undefined): boolean => {
  return (analysedProperty?.PropertyDetail?.PropertyDetailId ?? 0) > 0;
};

export const getCurrentFinancialYear = (): string => {
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  if (month < 6) {
    return `${year}`.toString().slice(-2);
  } else {
    return `${year + 1}`.toString().slice(-2);
  }
};

export const getStartOfFinancialYear = (): Date => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  return currentMonth < 6 ? new Date(currentYear - 1, 6, 1) : new Date(currentYear, 6, 1);
};

export const returnFormattedData = (data: number, prefix?: string, suffix?: string) => {
  return data < 0 ? (
    <span className="text-red-500">
      ({prefix}
      {Math.abs(data)}
      {suffix})
    </span>
  ) : (
    <span>
      {prefix}
      {data}
      {suffix}
    </span>
  );
};

export const makeSplitColorFn = (actualColor: string, forecastColor: string, actualLength: number) => {
  return (ctx: any): string => {
    const ds = ctx.dataset as any;
    const sliceStart = ds.sliceStartIndex ?? 0;
    const i = ctx.dataIndex + sliceStart;
    return i < actualLength ? actualColor : forecastColor;
  };
};

export const segmentColorBySplit = (actualColor: string, forecastColor: string, fullActualLength: number) => ({
  borderColor: (ctx: any): string => {
    const ds = ctx.chart.data.datasets[ctx.datasetIndex] as any;
    const sliceStart = ds.sliceStartIndex ?? 0;
    const i0 = ctx.p0DataIndex + sliceStart;
    return i0 < fullActualLength ? actualColor : forecastColor;
  },
});

export const colors = {
  actualMarket: "rgb(30, 64, 175)",
  forecastMarket: "rgb(102, 119, 136)",
  actualLoan: "rgb(185, 28, 28)",
  forecastLoan: "rgb(0, 170, 0)",
};

export const cashFlowColors = {
  actualIncome: "rgb(30, 64, 175)",
  forecastIncome: "rgb(102, 119, 136)",
  actualExpense: "rgb(185, 28, 28)",
  forecastExpense: "rgb(0, 170, 0)",
};

export const marketValueVsLoanColors = {
  marketValue: "rgb(220, 38, 38)",
  loan: "rgb(78,159,186)",
};

export const whoPaysColors = {
  you: "rgb(220, 38, 38)",
  taxman: "rgb(78,159,186)",
  tenants: "rgb(102,119,136)",
};

export const formatYAxisTick = (value: number): string => {
  const num = Math.round(Number(value));
  return "$" + num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
};

export const formatYearAsFY = (actualYear: number | string): string => {
  const yearStr = actualYear.toString();
  const lastTwoDigits = yearStr.slice(-2);
  const fy = `FY-${lastTwoDigits}`;
  const isYTD = lastTwoDigits === getCurrentFinancialYear();
  return isYTD ? `${fy} YTD` : fy;
};

export const convertToMMDDYYYY = (dateStr?: string | null): string | null => {
  if (!dateStr) return null;

  const parts = dateStr.split("/");
  if (parts.length !== 3) return null;

  const [day, month, year] = parts.map((p) => p.trim());

  if (!day || !month || !year || isNaN(Number(day)) || isNaN(Number(month)) || isNaN(Number(year))) {
    return null;
  }

  const paddedMonth = month.padStart(2, "0");
  const paddedDay = day.padStart(2, "0");

  return `${paddedMonth}/${paddedDay}/${year}`;
};

export const parseDate = (dateString: string): Date | null => {
  if (!dateString) return null;

  const parts = dateString.split("/");

  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);

    return new Date(year, month, day);
  }

  return null;
};

export const isMMDDYYFormat = (dateStr: string) => {
  return /^(0[1-9]|1[0-2])[\/-](0[1-9]|[12][0-9]|3[01])[\/-](\d{2}|\d{4})$/.test(dateStr);
};

export const shouldEnrichAddress = (address: string): boolean => {
  const trimmed = address.trim();
  if (!trimmed) return false;

  const hasPostcode = POSTCODE_REGEX.test(trimmed);
  const hasCountry = COUNTRY_REGEX.test(trimmed);

  return !hasPostcode || !hasCountry;
};

const getAddressComponent = (components: any[], type: string) =>
  components.find((component) => component.types?.includes(type));

const getFallbackStreet = (fallback?: string | null): string => {
  if (!fallback) return "";
  const firstSegment = fallback.split(",")[0];
  return (firstSegment || "").trim();
};

const hasUnitStreetPattern = (value?: string | null): boolean => {
  if (!value) return false;
  return /^\s*(?:unit|apt|apartment|level|lvl|lot)\s*\d+\/\d+\s+|^\s*\d+\/\d+\s+/i.test(value);
};

const getFallbackPostcode = (fallback?: string | null): string => {
  const match = fallback?.match(POSTCODE_REGEX);
  return match ? match[0] : "";
};

const getFallbackCountryCode = (fallback?: string | null): string => {
  const match = fallback?.match(COUNTRY_REGEX)?.[0];
  if (!match) return "";
  const normalized = match.toLowerCase();
  if (normalized === "au" || normalized === "australia") return "AU";
  if (normalized === "nz" || normalized === "new zealand") return "NZ";
  return "";
};

const buildStandardAddress = (
  components: any[],
  fallbackStreetSource?: string | null,
  fallbackMetaSource?: string | null
): string | null => {
  if (!components?.length) return fallbackMetaSource || fallbackStreetSource || null;

  const streetNumber = getAddressComponent(components, "street_number")?.long_name || "";
  const route = getAddressComponent(components, "route")?.long_name || "";
  const locality =
    getAddressComponent(components, "locality")?.long_name ||
    getAddressComponent(components, "sublocality")?.long_name ||
    getAddressComponent(components, "postal_town")?.long_name ||
    "";
  const state = getAddressComponent(components, "administrative_area_level_1")?.short_name || "";
  const postcode = getAddressComponent(components, "postal_code")?.long_name || "";
  const country = getAddressComponent(components, "country")?.short_name || "";

  const streetFromComponents = [streetNumber, route].filter(Boolean).join(" ").trim();
  const fallbackStreet = getFallbackStreet(fallbackStreetSource || fallbackMetaSource);
  const street = hasUnitStreetPattern(fallbackStreet) ? fallbackStreet : streetFromComponents || fallbackStreet;
  const resolvedPostcode = postcode || getFallbackPostcode(fallbackMetaSource);
  const resolvedCountry = country || getFallbackCountryCode(fallbackMetaSource);
  const parts = [street, locality, state, resolvedPostcode].filter(Boolean);

  if (!parts.length) return fallbackMetaSource || fallbackStreetSource || null;

  const base = parts.join(", ");
  return resolvedCountry ? `${base} (${resolvedCountry})` : base;
};

export const fetchFormattedAddress = async (
  address: string,
  apiKey: string,
  countryID?: number
): Promise<string | null> => {
  const country = Number(countryID) === 1 ? "AU" : Number(countryID) === 2 ? "NZ" : "";
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
    address
  )}&key=${apiKey}&components=country:${country}`;

  // try {
  //   const response = await fetch(url);
  //   if (!response.ok) return null;
  //   console.log("Google Maps API response received for address:", address);
  //   const data = await response.json();
  //   if (data.status === "OK" && data.results?.length > 0) {
  //     const result = data.results[0];
  //     const resultCountry = getAddressComponent(result.address_components || [], "country")?.short_name || "";
  //     if (resultCountry && resultCountry !== "AU" && resultCountry !== "NZ") {
  //       return null;
  //     }
  //     return buildStandardAddress(result.address_components || [], address, result.formatted_address || null);
  //   }
  // } catch {
  //   return null;
  // }

  return null;
};

export const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
};
